/**
 * Core concept fundamentals:
 * - Temporary layers are stored in 'temporaryLayers' layer group within Collection
 * - There is a list representation of these layers stored in Vuex
 * - KEEP ONE SOURCE OF TRUTH - every operation should be made directly on openlayers's layer.
 *   Collection events should update store accordingly, so avoid manipulating store.
 * - Use customLayer.js methods as often as possible. Methods here should extend them.
 */

import { sync } from 'vuex-pathify';
import { default_temporary_layer_style } from '@/assets/js/stylesDefinitions';

export default {
  computed: {
    temporaryLayers: sync('layers/temporaryLayers'),
  },
  methods: {
    /** Openlayers Listeners */

    handleTemporaryCollectionEvents(remove = false) {
      const tempCollection = this.getLayerById('temporaryLayers').getLayers();
      ['change', 'add', 'remove'].forEach(event =>
        tempCollection[remove ? 'un' : 'on'](event, this.onTemporaryCollectionChange)
      );
    },
    onTemporaryCollectionChange(e) {
      /** Layers array is reversed to represent order visually on the list */
      this.temporaryLayers = [...(e?.target?.getArray() ?? [])].reverse().map(layer => {
        return {
          name: layer.get('name'),
          id: layer.get('id'),
          isVisible: layer.getVisible(),
          geometry_type: layer.get('geometry_type'),
          style: layer.get('style_web'),
        };
      });
    },

    /** Temporary layers helpers */

    getTemporaryLayer(id) {
      const olTempLayer = this.getLayerById(id, 'temporaryLayers');
      if (!olTempLayer) throw new Error(`Temporary layer ID: ${id} not found`);
      return olTempLayer;
    },
    createTemporaryLayerName(name) {
      /**
       * Find existing layers and check if there is any with the same name.
       * Give new name a proper suffix if so, e.g. 'name (1)'
       * */
      const existingLayerNames = this.temporaryLayers.filter(layer => name === layer.id.split('__', 2)[1]);
      const newLayerSuffix = existingLayerNames.reduce(
        (acc, layer) => {
          const suffixMatch = layer.name.match(/\((\d+)\)$/);
          if (!suffixMatch?.[1]) return acc;
          const newSuffixNumber = parseInt(suffixMatch[1]) + 1;
          if (newSuffixNumber > acc) acc = newSuffixNumber;
          return acc;
        },
        !existingLayerNames.length ? 0 : 1
      );
      return newLayerSuffix > 0 ? `${name} (${newLayerSuffix})` : name;
    },
    toggleTemporaryLayerVisibility(id, value) {
      this.$root.$emit('toggleCustomVectorLayerVisibility', id, value, 'temporaryLayers');
      this.getLayerById('temporaryLayers')?.getLayers().dispatchEvent('change');
    },

    /** Main emitted methods */

    removeAllTemporaryLayers() {
      const tempGroup = this.getLayerById('temporaryLayers');
      tempGroup?.getLayers().clear();
    },

    /** Temporary layer CRUD */
    addTemporaryLayer({ geometry, name, index = undefined }) {
      /** Basic initial layer data */
      const id = `${new Date().toISOString()}__${name}`;
      const geometryType = (geometry?.geometry?.type ?? geometry.features?.[0]?.geometry?.type ?? '').toLowerCase();
      const style = default_temporary_layer_style[geometryType];

      if (!geometryType || !style) throw new Error('Error parsing new layer geometry');

      /** Add layer to Openlayers map with all required metadata */
      this.addCustomGeojsonLayer({
        id,
        geometry,
        group: 'temporaryLayers',
        groupIndex: index,
        layerParams: {
          layerConfig: {
            name: this.createTemporaryLayerName(name),
            style_web: style,
            geometry_type: geometryType,
          },
          zIndex: 1 /** Layer position should be based on index in Collection array */,
          renderImage: true /** Temporary layers are rendered via VectorImage to increase performance */,
          style: f => this.getFeatureStyle(f, style, geometryType),
        },
      });
    },
    removeTemporaryLayer(id) {
      this.addCustomGeojsonLayer({ id, group: 'temporaryLayers' });
    },
    /**
     * One global emit to make any changes in existing layer.
     * Add any additional actions here. Note that it works with Openlayers events.
     * Any uncommon action should dispatch Collection change event.
     * */
    alterTemporaryLayer(id, property, newValue) {
      const layer = this.getTemporaryLayer(id);
      switch (property) {
        case 'visibility':
          return this.toggleTemporaryLayerVisibility(layer.get('id'), newValue);
        default:
          throw new Error(`Property ${property} not exists`);
      }
    },
  },
  beforeDestroy() {
    this.removeAllTemporaryLayers();
    this.handleTemporaryCollectionEvents(true);
  },
  mounted() {
    this.$nextTick(() => this.handleTemporaryCollectionEvents());
    this.$root.$off('addTemporaryLayer');
    this.$root.$on('addTemporaryLayer', this.addTemporaryLayer);

    this.$root.$off('removeTemporaryLayer');
    this.$root.$on('removeTemporaryLayer', this.removeTemporaryLayer);

    this.$root.$off('alterTemporaryLayer');
    this.$root.$on('alterTemporaryLayer', this.alterTemporaryLayer);

    this.$root.$off('removeAllTemporaryLayers');
    this.$root.$on('removeAllTemporaryLayers', this.removeAllTemporaryLayers);
  },
};
